/**
 * Move within the compliance window (3.2, 4.2): pick a day inside the window and
 * a clinician; the pace / LUPA read and the three guardrail numbers update before
 * you commit. Opened from a worklist row it arrives on the clinician's next
 * working day inside the window, so the third option for an unfilled visit is one
 * confirmation. The guardrail warns and never blocks: only the past, a discipline
 * that cannot deliver the visit and a missing competency stop the move.
 */
import { useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { defaultMoveTarget, evaluateMove } from "../../engine/moveGuard";
import { rosterEntries } from "../../engine/roster";
import { moveWithinWindow } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import { OnPaceIndicator } from "./OnPaceIndicator";
import { useBranchClock } from "../../components/branchClock";

export function MoveWithinWindowDialog({ planVisitId, onClose }: { planVisitId: string; onClose: () => void }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const visit = state.planVisits[planVisitId];
  // Opened from a worklist row the day is the point: the same clinician's next
  // working day inside the window, at the time the visit already holds.
  const [suggested] = useState(() => defaultMoveTarget(state, planVisitId));
  const [toDate, setToDate] = useState(suggested?.date ?? visit?.targetDate ?? "");
  const [toHandle, setToHandle] = useState(visit?.providerHandle ?? "");
  const baseTime = suggested?.time ?? visit?.windowStart ?? "10:00";
  const [time, setTime] = useState(baseTime);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const clock = useBranchClock(tz);
  const ev = useMemo(() => (visit ? evaluateMove(state, planVisitId, toDate, toHandle || undefined) : undefined), [state, planVisitId, toDate, toHandle, visit]);
  if (!visit || !ev) return null;
  const candidates = rosterEntries(state).filter((e) => state.config.visitTypes[visit.visitTypeKind].disciplines.includes(e.discipline));
  const unchanged = toDate === visit.targetDate && (toHandle || visit.providerHandle) === visit.providerHandle && time === baseTime;
  const fmt = (d: string) => formatInBranchZone(dateKeyStart(d, tz), tz, "EEE d");
  const commit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await moveWithinWindow(planVisitId, toDate, toHandle || undefined, time);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Move within the compliance window
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {state.config.visitTypes[visit.visitTypeKind].label} · compliance window {fmt(ev.window.start)} – {fmt(ev.window.end)}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ev.days.map((d) => (
              <Chip key={d.date} label={fmt(d.date)} size="small" color={toDate === d.date ? "primary" : "default"} variant={toDate === d.date ? "filled" : "outlined"} disabled={!d.allowed && d.date !== visit.targetDate} onClick={() => setToDate(d.date)} title={d.reason && clock.sentence(d.reason)} data-day={d.date} data-allowed={d.allowed} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" select label="Clinician" value={toHandle} onChange={(e) => setToHandle(e.target.value)} sx={{ minWidth: 200 }}>
              {candidates.map((e) => (
                <MenuItem key={e.handle} value={e.handle}>
                  {e.displayName} · {e.discipline}
                  {visit.requiredTraits.length && !visit.requiredTraits.every((t) => e.traits.includes(t)) ? " · missing trait" : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="time" label="Arrival" value={time} onChange={(e) => setTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Pace after the move
            </Typography>
            <OnPaceIndicator pace={ev.paceAfter} />
          </Box>
          <Typography variant="body2" color="text.secondary" data-testid="move-checks">
            {[ev.checks.frequency && `Frequency this week ${ev.checks.frequency}`, ev.checks.periodRisk && `Visits at risk ${ev.checks.periodRisk}`, ev.checks.window && `Patient window ${clock.sentence(ev.checks.window)}`]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          {ev.reasons.length > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0 }} data-testid="move-blocked">
              {ev.reasons.map((r) => clock.sentence(r)).join(" · ")}
            </Alert>
          )}
          {error && (
            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={commit} disabled={busy || !ev.allowed || unchanged}>
          Move visit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
